import { useEffect, useMemo, useRef, useState } from "react";
import { Row, Col, Button, Card, ProgressBar, ButtonGroup, Nav, Form, Badge } from "react-bootstrap";
import useSWR from "swr";
import AuthenticatedWrapper from "../../components/wrapper";
import {
  fetcher,
  getParameterByName,
  INPUT_DATE_FORMAT,
  serverUrl,
  swrConfig,
  uploadPresignedFile,
} from "../../helpers/utility";
import { useForm } from "react-hook-form";
import axios from "axios";
import InputWithLabel from "../../components/inputWithLabel";
import { getGeocodeByPlaceId, getGeocodeWithArea, extractAreaName } from "../../helpers/maps";
import InputWithSelect from "../../components/inputWithSelect";
import GooglePlacesAutocomplete, { geocodeByAddress } from "react-google-places-autocomplete";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import CheckboxWithLabel from "../../components/CheckboxWithLabel";
import { getFiltersToSave, supportNeeds } from "../../helpers/supportNeeds";
import { EDUCATION, GENDER, NEEDS, DOCUMENT_TYPES, DOCUMENT_TYPE_LABELS } from "../../helpers/constants";
import NavigationBack from "../../components/NavigationBack";
import { Mic } from 'lucide-react';
import styles from '../../styles/addPlacement.module.scss';
import { CircleAlertIcon, TriangleAlertIcon } from 'lucide-react';
import { isValid } from "postcode";


function AddPlacement({ user, isGoogleMapLoaded }) {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    trigger,
    setValue,
    setError,
    clearErrors,
  } = useForm();
  const formValues = watch();
  let navigate = useNavigate();
  const placementId = getParameterByName("placementId");
  const steps = { GENERAL: "GENERAL", EDUCATION: "EDUCATION", SUPPORT: "SUPPORT" };
  const [step, setStep] = useState(steps.GENERAL);
  const [place, setPlace] = useState(null);
  const [educationAddress, setEducationAddress] = useState(null);
  const [showSupportNeeds, setShowSupportNeeds] = useState(false);
  const [fileUpload, setFileUpload] = useState(null);
  const [parsing, setParsing] = useState(false);
  const googlePlacesAutocompleteLocationRef = useRef();
  const googlePlacesAutocompleteEducationAddressRef = useRef();
  const [parseSuccess, setParseSuccess] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState("");
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [focusedField, setFocusedField] = useState(null);
  const [recordingField, setRecordingField] = useState(null);
  const recognitionRef = useRef(null);
  let placementData = {},
    placementError;

  // SWR conditional rendering not possible as it is a hook, use null to avoid making request
  // https://stackoverflow.com/questions/76625401/react-hook-useswr-is-called-conditionally-react-hooks-must-be-called-in-the-e
  const { data, error } = useSWR(
    placementId ? `${serverUrl}/placement/id/${placementId}` : null,
    fetcher,
    swrConfig
  );
  placementData = data;
  placementError = error;

  useEffect(() => {
    // Reloading data
    // Note specific formats for Date and Location
    if (placementData?.placement) {
      // Placement info section
      setValue("reference", placementData.placement.reference);
      setValue("firstname", placementData.placement.firstname);
      setValue("lastname", placementData.placement.lastname);
      setValue("comments", placementData.placement.comments);
      setValue("gender", placementData.placement.gender);
      setUploadedDocuments(placementData.placement.documents);
      // Location - use postcode instead of Google Places
      if (placementData.placement.location?.postcode) {
        setValue("postcode", placementData.placement.location.postcode);
      } else if (placementData.placement.location?.label) {
        // Fallback: try to extract postcode from label if postcode field doesn't exist
        const postcodeMatch = placementData.placement.location.label.match(/\b[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}\b/i);
        if (postcodeMatch) {
          setValue("postcode", postcodeMatch[0]);
        }
      }
      // Education address
      setEducationAddress({
        label: placementData.placement.educationAddress?.label,
        value: {
          place_id: placementData.placement.educationAddress?.placeId,
        },
      });
      if (placementData.placement.dob) {
        setValue("dob", format(placementData.placement.dob, INPUT_DATE_FORMAT));
      }
      if (placementData.placement.dateFrom) {
        setValue("dateFrom", format(placementData.placement.dateFrom, INPUT_DATE_FORMAT));
      }
      if (placementData.placement.dateTo) {
        setValue("dateTo", format(placementData.placement.dateTo, INPUT_DATE_FORMAT));
      }

      // Placement needs section
      setValue("overallNeeds", placementData.placement.overallNeeds);
      setValue("currentlyOnBail", placementData.placement.currentlyOnBail ? "Yes" : "No");

      // Set the filter values from the filters object
      if (Object.keys(placementData.placement.filters)?.length > 0) {
        setShowSupportNeeds(true);
        Object.keys(placementData.placement.filters).forEach((filterKey) => {
          setValue(filterKey, placementData.placement.filters[filterKey]);
        });
      }

      // Load Voice of the Child data if it exists
      if (placementData.placement.voiceOfTheChild) {
        const voc = placementData.placement.voiceOfTheChild;
        if (voc.preferredName) setValue("preferredName", voc.preferredName);
        if (voc.childsViewOfThemselves) setValue("childsViewOfThemselves", voc.childsViewOfThemselves);
        if (voc.whatChildWantsFromPlacement) setValue("whatChildWantsFromPlacement", voc.whatChildWantsFromPlacement);
        if (voc.whatMakesThemHappy) setValue("whatMakesThemHappy", voc.whatMakesThemHappy);
        if (voc.whatTheyLike) setValue("whatTheyLike", voc.whatTheyLike);
        if (voc.whatTheyDislike) setValue("whatTheyDislike", voc.whatTheyDislike);
      }
    }
  }, [placementData]);

  // Cleanup: Stop recording when leaving Voice of the Child step or unmounting
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
        recognitionRef.current = null;
      }
      setRecordingField(null);
    };
  }, [step]);

  // function handleSelectedFile(event) {
  //   if (!selectedDocumentType) {
  //     setUploadError("Please select a document type first.");
  //     return;
  //   }

  //   const newFileUpload = {
  //     selectedFiles: event.target.files,
  //     filesToUpload: event.target.files.length,
  //     filesUploaded: 0,
  //     loaded: 0,
  //     documentType: selectedDocumentType, // Add document type
  //   };

  //   setFileUpload(newFileUpload);
  //   handleUpload(event, newFileUpload);
  // }

  // const handleDeleteDocument = async (documentId, s3Key) => {
  //   try {
  //     const deleteDocumentUrl = `${serverUrl}/fileUpload/document`;

  //     const response = await axios.delete(deleteDocumentUrl, {
  //       data: {
  //         key: s3Key, // S3 key of the file to delete
  //         documentType: "Placement", // Database model type
  //         documentField: "documents", // Field name in the model
  //         documentId: placementData?.placement?._id, // Placement ID
  //       },
  //     });

  //     if (response && response.data && response.data.message) {
  //       // Remove from local state
  //       setUploadedDocuments((prev) => prev.filter((doc) => doc.externalId !== s3Key));
  //     } else {
  //       console.error("Error deleting document.");
  //       setUploadError("Failed to delete document. Please try again.");
  //     }
  //   } catch (error) {
  //     console.error("Error deleting document:", error);
  //     setUploadError("Failed to delete document. Please try again.");
  //   }
  // };

  // async function parseDocument(file) {
  //   // In production, you'd send this to your backend API
  //   // which would use libraries like pdf-parse, mammoth, or AI services

  //   return new Promise((resolve) => {
  //     setTimeout(() => {
  //       // Simulated parsed data - replace with actual API call
  //       const mockData = {
  //         personalInfo: {
  //           caseNumber: "1234567890",
  //           firstName: "George",
  //           lastName: "Jones",
  //           dob: "1990-01-01",
  //           gender: "MALE",
  //           location: {
  //             label: "20, Quarrington Road, Bristol, UK",
  //             placeId: "",
  //           },
  //           educationAddress: {
  //             label: "Orchard School Bristol, Filton Rd, Bristol BS7 0XZ",
  //             placeId: "",
  //           },
  //           dateFrom: "2026-01-01",
  //           dateTo: "2026-06-10",
  //           comments: "This is a test comment",
  //         },
  //       };
  //       resolve(mockData);
  //     }, 2000);
  //   });
  // }

  // async function handleFileUpload(e) {
  //   const uploadedFile = e.target.files[0];
  //   if (!uploadedFile) return;

  //   setFileUpload(uploadedFile);
  //   setParsing(true);
  //   setParseSuccess(false);

  //   try {
  //     const parsedData = await parseDocument(uploadedFile);

  //     // Auto-fill form with parsed data
  //     setValue("reference", parsedData.personalInfo.caseNumber || "");
  //     setValue("firstname", parsedData.personalInfo.firstName || "");
  //     setValue("lastname", parsedData.personalInfo.lastName || "");
  //     setValue("dob", format(parsedData.personalInfo.dob, INPUT_DATE_FORMAT));
  //     setValue("gender", parsedData.personalInfo.gender);

  //     setPlace({
  //       label: parsedData.personalInfo.location.label,
  //     });

  //     geocodeByAddress(parsedData.personalInfo.location.label).then((results) => {
  //       setPlace({
  //         label: parsedData.personalInfo.location.label,
  //         value: {
  //           place_id: results[0].place_id,
  //         },
  //       });
  //     });

  //     setEducationAddress({
  //       label: parsedData.personalInfo.educationAddress.label,
  //     });

  //     geocodeByAddress(parsedData.personalInfo.educationAddress.label).then((results) => {
  //       setEducationAddress({
  //         label: parsedData.personalInfo.educationAddress.label,
  //         value: {
  //           place_id: results[0].place_id,
  //         },
  //       });
  //     });

  //     setValue("dateFrom", format(parsedData.personalInfo.dateFrom, INPUT_DATE_FORMAT));
  //     setValue("dateTo", format(parsedData.personalInfo.dateTo, INPUT_DATE_FORMAT));
  //     setValue("comments", parsedData.personalInfo.comments);

  //     setParseSuccess(true);
  //   } catch (error) {
  //     console.error("Error parsing document:", error);
  //   } finally {
  //     setParsing(false);
  //   }
  // }

  // const handleViewDocument = async (doc) => {
  //   try {
  //     const response = await axios.post(`${serverUrl}/fileUpload/viewDocument`, {
  //       key: doc.externalId,
  //       documentType: "Placement",
  //       documentId: placementData?.placement?._id,
  //     });

  //     if (response.data.url) {
  //       // For PDFs - simple and reliable
  //       if (doc.contentType === "application/pdf") {
  //         window.open(response.data.url, "_blank");
  //       } else {
  //         // For other file types, trigger download
  //         window.location.href = response.data.url;
  //       }
  //     }
  //   } catch (error) {
  //     console.error("Error viewing document:", error);
  //     setUploadError("Failed to load document.");
  //   }
  // };

  // Handle upload directly to Amazon S3 Bucket
  // function handleUpload(event, fileObject) {
  //   event.preventDefault();
  //   console.log(`----- placementData`, placementData?.placement);
  //   const requestAWSUpload = `${serverUrl}/fileUpload/presignedUrl`;
  //   const registerDocumentUrl = `${serverUrl}/fileUpload/document`;
  //   const document = {
  //     documentType: "Placement",
  //     documentField: "documents",
  //     documentId: placementData?.placement?._id,
  //   };

  //   for (let file of fileObject.selectedFiles) {
  //     const fileMeta = {
  //       name: file.name,
  //       size: file.size,
  //       type: file.type,
  //       documentType: fileObject.documentType,
  //       acl: "private",
  //     };

  //     if (file.lastModified) {
  //       fileMeta.lastModified = new Date(file.lastModified);
  //     }

  //     // Tagging as raw flags to AWS that we can archive as infrequently accessed (50% cheaper)
  //     fileMeta.tagObject = "raw";

  //     axios
  //       .post(requestAWSUpload, fileMeta)
  //       .then((res) => {
  //         if (res && res.data && res.data.key && res.data.contentType && res.data.url) {
  //           uploadPresignedFile({
  //             presignedurl: res.data.url,
  //             file: file,
  //             onSuccess: () => {
  //               fileMeta.externalId = res.data.key;
  //               fileMeta.contentType = res.data.contentType;

  //               axios.post(registerDocumentUrl, { fileMeta, document }).then((res) => {
  //                 if (res && res.data && res.data.key && res.data.url) {
  //                   console.log("Registered document.");
  //                   // fileObject.filesUploaded++;
  //                   // fileObject.loaded += file.size;

  //                   // Enhanced: Add document with type information
  //                   const newDocument = {
  //                     id: res.data.key,
  //                     name: fileMeta.name,
  //                     size: fileMeta.size,
  //                     type: fileMeta.type,
  //                     documentType: fileObject.documentType, // Include document type
  //                     uploadedAt: new Date().toISOString(),
  //                     url: res.data.url,
  //                   };

  //                   setUploadedDocuments((prev) => [...prev, newDocument]);
  //                   setUploadSuccess(true);
  //                   setTimeout(() => setUploadSuccess(false), 3000);

  //                   // TODO
  //                 } else {
  //                   console.error("Error.");
  //                 }
  //               });
  //             },
  //             onFail: null,
  //             onProgress: (ProgressEvent) => {
  //               // TODO
  //             },
  //           });
  //         }
  //       })
  //       .catch((error) => {
  //         console.log(error);
  //       });
  //   }
  // }

  async function nextStep() {
    clearErrors();

    if (step === steps.GENERAL) {
      // Note: Can use array of field names instead of validating 'everything'
      let validationOK = await trigger();

      // Postcode validation is handled by the form validation rules

      if (validationOK !== true) {
        window.scrollTo(0, 0);
        return false;
      }

      setStep(steps.EDUCATION);
      // const filters = getFiltersToSave(data);
      // data.filters = filters;
      // const placementResponse = await axios.put(`${serverUrl}/placement`, placementData.);
      return true;
    }

    if (step === steps.EDUCATION) {
      // Note: Can use array of field names instead of validating 'everything'
      let validationOK = await trigger();

      if (validationOK !== true) {
        window.scrollTo(0, 0);
        return false;
      }
      
      geocodeByAddress(educationAddress.label).then((results) => {
        if (results && results.length > 0 && results[0].types.includes("school") || results[0].types.includes("university")) {
          setStep(steps.SUPPORT);
          return true;
        } else {
          setStep(steps.EDUCATION);
          setError("educationAddress", {
            type: "custom",
            message: "Must be a valid education address.",
          });
          return false;
        }
      }).catch((error) => {
        console.error("Error geocoding education address:", error);
        setError("educationAddress", { type: "custom", message: "Must be a valid education address." });
        return false;
      });
    }

    if (step === steps.SUPPORT) {
      // Note: Can use array of field names instead of validating 'everything'
      let validationOK = await trigger();

      if (validationOK !== true) {
        window.scrollTo(0, 0);
        return false;
      }

      // Immediately submit
      setStep(steps.VOICE_OF_THE_CHILD);
    }

    if (step === steps.VOICE_OF_THE_CHILD) {
      // Note: Can use array of field names instead of validating 'everything'
      let validationOK = await trigger();

      if (validationOK !== true) {
        window.scrollTo(0, 0);
        return false;
      }

      // Immediately submit
      handleSubmit(onSubmit)();
    }
  }

  // Get accepted file types based on document category
  function getAcceptTypes(documentType) {
    switch (documentType) {
      case DOCUMENT_TYPES.PHOTO:
        return "image/*";
      case DOCUMENT_TYPES.MEDICAL:
      case DOCUMENT_TYPES.EDUCATIONAL:
      case DOCUMENT_TYPES.COURT:
      case DOCUMENT_TYPES.REFERRAL:
        return ".pdf,.doc,.docx,.txt";
      default:
        return "*";
    }
  }

  // Format file size for display
  function formatFileSize(bytes) {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }

  // Child info
  function formGeneralInformation() {
    return (
      <>
        <div className={styles.formHeader}>
          <h1 className="h3">
            {placementData?.placement?.firstname ? `Update Placement` : `Add Placement`}
          </h1>
        </div>

        <div>
          <InputWithLabel
            inputId={`reference`}
            label={`Reference`}
            placeholder="e.g. Case number"
            type="text"
            register={register}
            error={errors.reference}
            errorMessage={`Required. Enter at least 3 characters.`}
            validation={{ required: true, minLength: 3 }}
          />
          <div className={styles.formRow}>
            <div className="d-flex flex-column w-50">
              <InputWithLabel
                wrapperClass="mb-0"
                inputId={`firstname`}
                label={`First name, Initials or Known as`}
                placeholder=""
                type="text"
                register={register}
                error={errors.reference}
                errorMessage={`Required. Enter at least 3 characters.`}
                validation={{ required: true, minLength: 3 }}
              />
              <p className={styles.helpText}>
                *this will <strong>not</strong> be shared with potential care providers.
              </p>
            </div>
            {/* <div className="d-flex flex-column w-50 ms-2">
              <InputWithLabel
                inputId={`lastname`}
                label={`Last name`}
                placeholder=""
                type="text"
                register={register}
              />
            </div> */}
          </div>

          <div className={styles.formRow}>
            <InputWithSelect
              wrapperClass="w-50"
              inputId={`gender`}
              label={"Gender"}
              register={register}
              options={Object.keys(GENDER).map((key) => {
                return { value: key, label: GENDER[key] };
              })}
            />

            <InputWithLabel
              wrapperClass="ms-2 w-50"
              inputId={`dob`}
              label={`Date of Birth`}
              type="date"
              register={register}
            />
          </div>

          {/* <div className={styles.formGroup}>
            <label htmlFor="location" className="form-label">
              Location
            </label>
            <div className={styles.googlePlacesWrapper}>
              {isGoogleMapLoaded ? (
                <GooglePlacesAutocomplete
                  ref={googlePlacesAutocompleteLocationRef}
                  debounce={500}
                  selectProps={{
                    value: place,
                    onChange: setPlace,
                    placeholder: "Enter location",
                    styles: {
                      control: (provided, state) => ({
                        ...provided,
                        borderRadius: "6px",
                        borderColor: state.isFocused ? "#006260" : "#e7eeee",
                        borderWidth: "1.5px",
                        boxShadow: state.isFocused ? "0 0 0 3px rgba(0, 98, 96, 0.1)" : "none",
                        "&:hover": {
                          borderColor: "#006260",
                        },
                      }),
                      valueContainer: (provided) => ({
                        ...provided,
                        padding: "10px 14px",
                      }),
                      container: (provided) => ({
                        ...provided,
                        paddingTop: 0,
                        paddingBottom: 0,
                      }),
                      input: (provided) => ({
                        ...provided,
                        fontSize: 14,
                        paddingTop: 0,
                        paddingBottom: 0,
                        margin: 0,
                      }),
                      placeholder: (provided) => ({
                        ...provided,
                        color: "#b9b9b9",
                        fontStyle: "italic",
                      }),
                    },
                  }}
                  onLoadFailed={(error) => {
                    console.error(`ERROR: ${error}`);
                  }}
                  minLengthAutocomplete={3}
                />
              ) : null}
            </div>
            {errors?.place ? (
              <label className={styles.errorMessage}>
                {errors.place?.message || "Required field"}
              </label>
            ) : null}
          </div> */}
          <div className="row gx-2">
            <InputWithLabel
              wrapperClass="mb-3 col"
              inputId={`postcode`}
              error={errors.postcode}
              errorMessage={`A valid postcode is required.`}
              validation={{
                required: true,
                validate: (value) => (isValid(value) ? true : "Invalid postcode"),
              }}
              label={`Postcode where the child is currently located`}
              placeholder="i.e BN1 1AA"
              type="text"
              register={register}
            />
          </div>
          <div className="row gx-2">
            <InputWithLabel
              wrapperClass="mb-3 col"
              inputId={`dateFrom`}
              error={errors.dateFrom}
              errorMessage={`A valid placement start date is required.`}
              validation={{ required: true }}
              label={`Placement start date`}
              placeholder="The target date for the placement to begin."
              type="date"
              register={register}
            />
             <div className="d-flex flex-column w-50">
              <InputWithLabel
                wrapperClass="col"
                inputId={`dateTo`}
                label={`Placement end date`}
                placeholder="Optional. The end date of a placement if known."
                type="date"
                register={register}
              />
              <p className={styles.helpText}> 
                *leave blank if not known
              </p>
            </div>
          </div>

          <InputWithLabel
            inputId={`comments`}
            label={`Additional details`}
            placeholder="Any comments or support needs you wish to share with care providers."
            type="textarea"
            inputProps={{ rows: 6 }}
            register={register}
          />
        </div>
      </>
    );
  }

  // Child education
  function formChildEducation() {
    return (
      <>
        <div className={styles.formHeader}>
          <h1 className="h3">
            {formValues?.firstname ? `${formValues?.firstname}'s ` : null}Education
          </h1>
        </div>

        <div>
          <InputWithSelect
            inputId={`education`}
            label={"Education status"}
            register={register}
            options={[
              {
                value: EDUCATION.FULL_TIME,
                label: "Full time education",
              },
              {
                value: EDUCATION.PART_TIME,
                label: "Part time education",
              },
              {
                value: NEEDS.NOT_IN_EDUCATION,
                label: "Not in education",
              },
            ]}
          />

          <div className={styles.formGroup}>
            <label htmlFor="educationAddress" className="form-label">Education Address</label>
            <div className={styles.googlePlacesWrapper}>
              <GooglePlacesAutocomplete
                ref={googlePlacesAutocompleteEducationAddressRef}
                debounce={500}
                selectProps={{
                  value: educationAddress,
                  onChange: setEducationAddress,
                  placeholder: "Enter education address, school or college",
                  styles: {
                    control: (provided, state) => ({
                      ...provided,
                      borderRadius: "6px",
                      borderColor: state.isFocused ? "#006260" : "#e7eeee",
                      borderWidth: "1.5px",
                      boxShadow: state.isFocused ? "0 0 0 3px rgba(0, 98, 96, 0.1)" : "none",
                      "&:hover": {
                        borderColor: "#006260",
                      },
                    }),
                    valueContainer: (provided) => ({
                      ...provided,
                      padding: "10px 14px",
                    }),
                    placeholder: (provided) => ({
                      ...provided,
                      color: "#b9b9b9",
                      fontStyle: "italic",
                    }),
                  },
                }}
              />
            </div>
            {errors?.educationAddress ? <label className={styles.errorMessage}>{errors.educationAddress?.message || 'Required field'}</label> : null}
          </div>

          <div className="mb-3">
            <CheckboxWithLabel
              inputId={"ehcp"}
              label={"EHCP"}
              type="checkbox"
              register={register}
              wrapperClass={"check-row-wrapper"}
              inputProps={{ className: null }}
            />
          </div>
        </div>
      </>
    );
  }

  // Child needs
  function formChildNeeds() {
    return (
      <>
        <div className={styles.formHeader}>
          <h1 className="h3">
            {formValues?.firstname ? `${formValues?.firstname}'s ` : null}Support Needs
          </h1>
        </div>

        <div style={{ marginBottom: 30 }}>
          <InputWithSelect
            inputId={`overallNeeds`}
            label={"Overall"}
            register={register}
            options={[
              {
                value: NEEDS.LOW,
                label: "Low level needs",
              },
              {
                value: NEEDS.MEDIUM,
                label: "Medium level needs",
              },
              {
                value: NEEDS.HIGH,
                label: "High level needs",
              },
            ]}
          />

          {formValues?.overallNeeds && (
            <p className={styles.helpText}>
              {formValues?.overallNeeds === NEEDS.LOW
                ? `${formValues?.firstname} is at the expected developmental age and ability, requires minimal intervention.`
                : formValues?.overallNeeds === NEEDS.MEDIUM
                ? `${formValues?.firstname} requires support during the week but this isn't a daily occurrence.`
                : formValues?.overallNeeds === NEEDS.HIGH
                ? `${formValues?.firstname} requires intensive support daily.`
                : null}
            </p>
          )}

          <div className={"mb-3"}>
            <InputWithSelect
              inputId={`currentlyOnBail`}
              label={"Currently on bail? (Yes or No)"}
              register={register}
              options={[
                { value: "Yes", label: "Yes" },
                { value: "No", label: "No" },
              ]}
            />
          </div>

          <div className={styles.supportSection}>
            <h1 className="h4">
              Support Details{" "}
              <span className={styles.optionalLabel}>
                (Optional)
              </span>
            </h1>

            <p>
              Would you like to provide a breakdown of {formValues?.firstname}'s support needs? This
              will not impact your search results but could help to save time later.
            </p>
            <div className={styles.supportToggle}>
              <div className={styles.buttonGroup}>
                <button
                  type="button"
                  className={showSupportNeeds === false ? styles.active : ''}
                  onClick={() => {
                    setShowSupportNeeds(false);
                  }}
                >
                  Skip
                </button>
                <button
                  type="button"
                  className={showSupportNeeds === true ? styles.active : ''}
                  onClick={() => {
                    setShowSupportNeeds(true);
                  }}
                >
                  Include
                </button>
              </div>
            </div>
          </div>

          {/* Support needs */}
          {Object.keys(supportNeeds)?.length > 0
            ? Object.keys(supportNeeds).map((supportNeedKey) => {
                const supportNeedsItem = supportNeeds[supportNeedKey];

                return (
                  <div
                    style={{
                      display: showSupportNeeds === true ? "block" : "none",
                      marginBottom: 15,
                    }}
                    key={supportNeedsItem.key}
                  >
                    <h1 className="h4" style={{ marginTop: 30 }}>
                      {supportNeedsItem.title}
                    </h1>

                    <div className={styles.checkboxSection}>
                      {Object.keys(supportNeedsItem.items)?.length > 0
                        ? Object.keys(supportNeedsItem.items)
                            .sort()
                            .map((risk) => {
                              return (
                                <CheckboxWithLabel
                                  inputId={risk}
                                  label={supportNeedsItem.items[risk]}
                                  type="checkbox"
                                  register={register}
                                  wrapperClass={"check-row-wrapper"}
                                  inputProps={{ className: null }}
                                />
                              );
                            })
                        : null}
                    </div>
                  </div>
                );
              })
            : null}
        </div>
      </>
    );
  }

  const handleVoiceInput = (fieldId) => {
    // Stop any existing recording
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }

    // If clicking the same field that's recording, stop recording
    if (recordingField === fieldId) {
      setRecordingField(null);
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert("Speech recognition is not supported in your browser.");
      return;
    }

    const recognition = new SpeechRecognition();
    // recognition.interimResults = true;
    recognition.lang = "en-GB";
    recognition.continuous = false;

    recognition.onresult = (event) => {
      let finalTranscript = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript + ' ';
        }
      }
      if (finalTranscript) {
        // Get current value from form state using getValues
        // const currentValue = getValues(fieldId) || "";
        const newValue = finalTranscript.trim();
        setValue(fieldId, newValue, { shouldDirty: true, shouldValidate: true });
      }
    };

    recognition.onerror = (event) => {
      console.error("Speech recognition error:", event.error);
      if (event.error === 'no-speech') {
        // This is normal when user stops speaking, don't show error
        return;
      }
      setRecordingField(null);
    };

    recognition.onend = () => {
      setRecordingField(null);
      recognitionRef.current = null;
    };

    recognitionRef.current = recognition;
    setRecordingField(fieldId);
    recognition.start();
  };

  function formVoiceOfTheChild() {
    const voiceFields = [
      {
        id: "preferredName",
        label: `${formValues?.firstname ? `${formValues?.firstname} ` : "Child"} would like to be called`,
        placeholder: "Preferred name",
        type: "text",
      },
      {
        id: "childsViewOfThemselves",
        label: `${formValues?.firstname ? `${formValues?.firstname} ` : "Child"} views themselves as`,
        placeholder: "Child's view of themselves",
        type: "textarea",
        inputProps: { rows: 6 },
      },
      {
        id: "whatChildWantsFromPlacement",
        label: `${formValues?.firstname ? `${formValues?.firstname} ` : "Child"} would like to be placed in a home that`,
        placeholder: "What does the child want from the placement they are moving to",
        type: "textarea",
        inputProps: { rows: 6 },
      },
      {
        id: "whatMakesThemHappy",
        label: `${formValues?.firstname ? `${formValues?.firstname} ` : "Child"} feels happiest when`,
        placeholder: "What makes them happy",
        type: "textarea",
        inputProps: { rows: 6 },
      },
      {
        id: "whatTheyLike",
        label: `${formValues?.firstname ? `${formValues?.firstname} ` : "Child"} would like you to know that they like`,
        placeholder: "What do they like",
        type: "textarea",
        inputProps: { rows: 6 },
      },
      {
        id: "whatTheyDislike",
        label: `${formValues?.firstname ? `${formValues?.firstname} ` : "Child"} would like you to know that they don't like`,
        placeholder: "What do they dislike",
        type: "textarea",
        inputProps: { rows: 6 },
      },
    ];

    return (
      <>
        <div className={styles.formHeader}>
          <h1 className="h3">
            {`In ${formValues?.firstname ? `${formValues?.firstname}'s` : "their own"} words`}
          </h1>
        </div>

        <div className={styles.formSection}>
          {voiceFields.map((field) => (
            <div key={field.id} className={styles.formGroup} style={{ position: "relative" }}>
              <label htmlFor={field.id} className="form-label">{field.label}</label>
              <div style={{ position: "relative" }}>
                {(focusedField === field.id || recordingField === field.id) && (
                  <button
                    type="button"
                    className={`${styles.voiceInputButton} ${recordingField === field.id ? styles.recording : ''}`}
                    onClick={() => handleVoiceInput(field.id)}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <Mic size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                    {recordingField === field.id ? "Stop" : "Record"}
                  </button>
                )}
                <Form.Control
                  type={field.type}
                  {...register(field.id)}
                  className="form-control"
                  placeholder={field.placeholder}
                  {...field.inputProps}
                  as={field.type === "textarea" ? field.type : undefined}
                  onFocus={() => setFocusedField(field.id)}
                  onBlur={() => {
                    // Delay to allow mic button click
                    // setTimeout(() => setFocusedField(null), 200);
                  }}
                  id={field.id}
                />
              </div>
              {errors?.[field.id] && (
                <label className={styles.errorMessage}>
                  {errors[field.id]?.message || 'Required field'}
                </label>
              )}
            </div>
          ))}
        </div>
      </>
    );
  }

  /*eslint no-unreachable: "error"*/
  async function onSubmit(data) {
    try {
      // Ensure ID is passed across so can update same document
      if (placementId) {
        data._id = placementId;
      }

      const filters = getFiltersToSave(data);
      data.filters = filters;

      // Extract Voice of the Child data into structured object
      data.voiceOfTheChild = {
        preferredName: data.preferredName || null,
        childsViewOfThemselves: data.childsViewOfThemselves || null,
        whatChildWantsFromPlacement: data.whatChildWantsFromPlacement || null,
        whatMakesThemHappy: data.whatMakesThemHappy || null,
        whatTheyLike: data.whatTheyLike || null,
        whatTheyDislike: data.whatTheyDislike || null,
      };

      // Process location information from postcode
      if (data.postcode) {
        if (!isGoogleMapLoaded) {
          console.error(`Google maps has not yet loaded for AddPlacement, lookup failed!`);
        }

        try {
          const geocodeResults = await geocodeByAddress(data.postcode);
          
          if (geocodeResults && geocodeResults.length > 0) {
            const geocodeResult = geocodeResults[0];
            const areaName = extractAreaName(geocodeResult);
            
            data.location = {
              postcode: data.postcode,
              label: geocodeResult.formatted_address || data.postcode,
              placeId: geocodeResult.place_id,
              lat: geocodeResult.geometry.location.lat(),
              lng: geocodeResult.geometry.location.lng(),
              areaName: areaName || null,
            };
          } else {
            console.error(`Could not geocode postcode: ${data.postcode}`);
            alert(`Could not find location for postcode: ${data.postcode}`);
            return false;
          }
        } catch (error) {
          console.error(`Error geocoding postcode: ${error}`);
          alert(`Error finding location for postcode: ${data.postcode}`);
          return false;
        }

        if (educationAddress && educationAddress.value?.place_id) {
          const educationAddressResults = await getGeocodeByPlaceId({
            isLoaded: isGoogleMapLoaded,
            placeId: educationAddress.value?.place_id,
          });

          if (educationAddressResults) {
            data.educationAddress = {
              label: educationAddress.label,
              placeId: educationAddress.value?.place_id,
              lat: educationAddressResults.lat,
              lng: educationAddressResults.lng,
            };
            console.log(`----- data.educationAddress:`, data.educationAddress);
          } else {
            console.error(`Could not geocode education address: ${educationAddress.label}`);
          }
        }
      } else {
        console.error(`Please add a postcode for this placement.`);
        alert(`Please add a postcode for this placement.`);
        return false;
      }

      // Convert any non-string data
      if (typeof data.dob === "string" && data.dob?.length > 4) {
        data.dob = new Date(data.dob);
      }
      if (typeof data.dateFrom === "string" && data.dateFrom?.length > 4) {
        data.dateFrom = new Date(data.dateFrom);
      }
      if (typeof data.dateTo === "string" && data.dateTo?.length > 4) {
        data.dateTo = new Date(data.dateTo);
      }      

      // Update insights
      const now = new Date();
      const periodKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
      await axios.put(`${serverUrl}/insight/${user.localAuthorityId}`, {
        localAuthorityId: user.localAuthorityId,
        periodKey: periodKey,
        periodType: "monthly",
        searchedArea: {
          label: data.location?.areaName || data.location?.label || "Unknown Area",
        },
        risksSearched: filters,
        individualNeeds: filters,
      });


      // Updating course section
      const placementResponse = await axios.put(`${serverUrl}/placement`, data);

      console.log(`----- placementResponse:`, placementResponse);

      if (placementResponse && placementResponse.data && placementResponse.data.success) {
        console.log(`----- Updated placement`);
        const placement = placementResponse.data.result;
        const placementId = placement._id;
        const searchRedirectURL = `/search?placementId=${placementId}&lat=${placement.location?.lat}&lng=${placement.location?.lng}&newPlacement=true`;

        if (placementResponse.data?.isNewPlacement) {
          // New placement, progress to search page
          navigate(searchRedirectURL);
        } else {
          // Existing placement updated, go back to the dashboard
          alert(`Placement has been updated.`);
          navigate(-1);
        }
      } else {
        alert(`Could not save.`);
      }
    } catch (error) {
      console.error(`Error submitting add-placement form. ${error}`);
    }
  }

  const stepProgress = useMemo(() => {
    if (step === steps.GENERAL) {
      return 0;
    }

    if (step === steps.EDUCATION) {
      return 33;
    }

    if (step === steps.SUPPORT) {
      return 66;
    }

    if (step === steps.VOICE_OF_THE_CHILD) {
      return 100;
    }
  }, [step]);

  return (
    <AuthenticatedWrapper user={user}>
      <NavigationBack />

      <Row
        className={`d-flex justify-content-center ${styles.addPlacementContainer}`}
        style={{ padding: "0px 0px 30px 0px" }}
      >
        <Col xs={12} lg={9} xl={6}>
          <Nav variant="tabs" className={styles.formTabs}>
            <Nav.Item className={styles.navItem}>
              <Nav.Link
                className={
                  step === steps.GENERAL ? styles.active + " " + styles.navLink : styles.navLink
                }
                onClick={() => {
                  console.log(`GENERAL`);
                  setStep(steps.GENERAL);
                }}
                active={step === steps.GENERAL}
              >
                General
              </Nav.Link>
            </Nav.Item>
            <Nav.Item className={styles.navItem}>
              <Nav.Link
                className={
                  step === steps.EDUCATION ? styles.active + " " + styles.navLink : styles.navLink
                }
                onClick={() => {
                  console.log(`Education`);
                  setStep(steps.EDUCATION);
                }}
                active={step === steps.EDUCATION}
              >
                Education
              </Nav.Link>
            </Nav.Item>
            <Nav.Item className={styles.navItem}>
              <Nav.Link
                className={
                  step === steps.SUPPORT ? styles.active + " " + styles.navLink : styles.navLink
                }
                onClick={() => {
                  console.log(`SUPPORT`);
                  setStep(steps.SUPPORT);
                }}
                active={step === steps.SUPPORT}
              >
                Support Needs
              </Nav.Link>
            </Nav.Item>
            <Nav.Item className={styles.navItem}>
              <Nav.Link
                className={
                  step === steps.VOICE_OF_THE_CHILD
                    ? styles.active + " " + styles.navLink
                    : styles.navLink
                }
                onClick={() => {
                  console.log(`Voice of the Child`);
                  setStep(steps.VOICE_OF_THE_CHILD);
                }}
                active={step === steps.VOICE_OF_THE_CHILD}
              >
                Voice of the Child
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <Card className={styles.formCard}>
            <Card.Body className={styles.cardBody}>
              {/* Do not use onSubmit, will triger submit on enter, not wanted! */}
              <form>
                {
                  // Progress bar is only shown for NEW placements
                  !placementData?.placement?._id ? (
                    <div className={styles.progressContainer}>
                      <div className={styles.progressBar}>
                        <div
                          className={styles.progressFill}
                          style={{ width: `${stepProgress}%` }}
                        />
                      </div>
                    </div>
                  ) : null
                }

                {step === steps.GENERAL ? formGeneralInformation() : null}

                {step === steps.EDUCATION ? formChildEducation() : null}

                {step === steps.SUPPORT ? formChildNeeds() : null}

                {step === steps.VOICE_OF_THE_CHILD ? formVoiceOfTheChild() : null}

                <div style={{ marginTop: 30, paddingTop: 20, borderTop: "1px solid #e7eeee" }}>
                  <button type="button" className={styles.nextButton} onClick={nextStep}>
                    {step !== steps.VOICE_OF_THE_CHILD ? "NEXT" : "FINISH"}
                  </button>
                </div>
              </form>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} lg={3} xl={4}>
          {/* Placement Process Section */}

          {/* <h4 className="mt-3 mb-3">
            <Badge bg="warning" className="p-3" pill>
               <CircleAlertIcon size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Progress not saved!
            </Badge>
          </h4> */}
          <Card className={`mobile-gap ${styles.infoCard}`}>
            <Card.Body className={styles.cardBody}>
              <h1 className="h3">Placement Process</h1>

              <p>It is easy to find the best care providers for every child with bPlaced.</p>

              <h1 className="h3" style={{ marginTop: 25 }}>
                Steps
              </h1>

              <p>
                1. Add information about the child that can be used to help filter providers and
                optionally be included in any enquiries.
              </p>
              <p>
                2. Search for care providers by location. Availability is regularly updated by our
                approved providers, you can also easily filter based on the child's individual
                needs.
              </p>
              <p>
                3. Make an enquiry to one or more providers via bPlaced. We will include all
                required information to make the process as simple as possible.
              </p>
              <p>4. Track the placement and finalise details with the provider.</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </AuthenticatedWrapper>
  );
}

export default AddPlacement;
